/**
 * The backdrop that makes a passthrough session opaque again.
 *
 * This exists for one device-shaped reason. `render/xr.ts` asks for
 * `immersive-vr` and falls back to `immersive-ar`, and an AR session composites
 * the frame **over the room the player is standing in**. For most WebXR content
 * that is the point. For this game it is the end of it: the premise is being
 * nowhere, in a building with no outside, and a sofa showing faintly through the
 * yellow wallpaper is a promise that you are fine and this will be over in a
 * minute.
 *
 * It cannot be fixed with `scene.background`. three's `WebGLBackground.render`
 * reads `renderer.xr.getEnvironmentBlendMode()` **after** it has taken the
 * scene's background colour, and on `alpha-blend` it overrides the clear with
 * `(0, 0, 0, 0)` — deliberately, so that ordinary AR content shows the room
 * wherever it has drawn nothing. A `Color` background contributes no geometry,
 * only that clear, so in a passthrough session it contributes nothing at all.
 *
 * That matters most exactly where the game is most exposed. Indoors the level's
 * own walls and ceiling cover every pixel, so a leak would be a rim around the
 * geometry. Level 94 and the Endless City are `Cell.Open`: the lanes have no
 * roof, and what stands in for sky up there is *only* the clear colour. In an AR
 * session the entire sky over the suburb would be the player's own ceiling.
 *
 * So the sky becomes a real surface: an inward-facing box locked to the head,
 * drawn before everything else with depth testing off, in the colour the flat
 * game clears to. Nothing else in the frame changes, and no other file has to
 * know which kind of session it is in.
 */

import * as THREE from 'three';

/**
 * Half-extent of the box, metres.
 *
 * Only two things constrain it. It has to clear the near plane
 * (`TUNING.render.near`, 0.05 m) by enough that a stereo eye — offset about
 * 3 cm from the mono camera this is parented to — can never push a face behind
 * it, and it has to stay inside the far plane (120 m). Four metres is
 * comfortably clear of both, and because depth testing is off the distance has
 * no other consequence: the box is a colour, not a place.
 */
const HALF_EXTENT = 4;

/**
 * Drawn before anything the game builds. three sorts the opaque list by
 * `renderOrder` first, and every mesh in this game leaves it at 0.
 */
const RENDER_ORDER = -10000;

/** The opaque surround. One instance lives for the length of a session. */
export interface XREnvironment {
  /** Parent this to the camera, as `render/xrvignette.ts`'s tunnel is. */
  readonly object: THREE.Object3D;
  /**
   * Take the backdrop's colour from the scene the session is drawing.
   *
   * Cheap enough to call every frame, which is what the caller does: the fog
   * colour changes when a level is built, and a session outlives several.
   */
  syncTo(scene: THREE.Scene): void;
  dispose(): void;
}

/**
 * Build the surround. Nothing is added to any scene and no geometry is
 * allocated until this is called, so a flat game — and a headset session that
 * turned out to be opaque — never owns it.
 */
export function makeXREnvironment(): XREnvironment {
  const material = new THREE.MeshBasicMaterial({
    color: 0x000000,
    // The camera is inside it, so only the inward faces are ever wanted.
    side: THREE.BackSide,
    // Nothing may hide the sky, and the sky may hide nothing: it is drawn
    // first, it writes no depth, and every surface in the level then draws
    // over it exactly as it would have drawn over the clear colour.
    depthTest: false,
    depthWrite: false,
    // Fogging the fog colour would only darken it toward itself, and the
    // distance it would be fogged at is meaningless — see `HALF_EXTENT`.
    fog: false,
    // `transparent` is deliberately left false. three drops an opaque
    // `NormalBlending` material to `NoBlending` in `WebGLState.setMaterial`,
    // so this writes alpha 1 straight into the eye buffer rather than
    // compositing against whatever the compositor put there. That single
    // detail is what actually shuts the passthrough out.
    transparent: false,
    // Left tone-mapped on purpose: see `syncTo`.
    toneMapped: true,
  });

  const geometry = new THREE.BoxGeometry(HALF_EXTENT * 2, HALF_EXTENT * 2, HALF_EXTENT * 2);
  const mesh = new THREE.Mesh(geometry, material);
  // It is always around the head, by construction. Culling it against a frustum
  // it necessarily encloses is both wasted work and a way to lose it.
  mesh.frustumCulled = false;
  mesh.renderOrder = RENDER_ORDER;
  // Identity, forever: it inherits the camera's world matrix and wants nothing
  // of its own. Twelve triangles is not a budget worth recomposing a matrix for.
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();

  const fallback = new THREE.Color(0x000000);

  return {
    object: mesh,

    syncTo(scene: THREE.Scene): void {
      /*
       * The fog colour, raw, with tone mapping left on.
       *
       * `render/renderer.ts` clears to `acesFilmic(fogColour)` because three
       * writes the clear colour straight to the framebuffer without running it
       * through the tone-mapping the rest of the frame gets. A `MeshBasicMaterial`
       * is not a clear colour — it goes through the fragment shader like every
       * wall — so it takes the fog colour *before* that transform and comes out
       * the other side as the identical pixel. Reading `scene.background`
       * instead would tone-map an already tone-mapped colour and the sky would
       * come out visibly paler than the fog it is supposed to be the far end of.
       */
      const fog = scene.fog;
      if (fog) material.color.copy(fog.color);
      else if (scene.background instanceof THREE.Color) material.color.copy(scene.background);
      else material.color.copy(fallback);
    },

    dispose(): void {
      mesh.parent?.remove(mesh);
      geometry.dispose();
      material.dispose();
    },
  };
}
